import { Grid, type SxProps } from "@mui/material";
import { FormDataConsumer, required, useRecordContext } from "react-admin";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router";
import {
    AutocompleteInput,
    EnumInput,
    MapField,
    NullableBooleanInput,
    ReferenceInput,
    TextInput
} from "../../components";
import type { Location } from "../../models";
import { Country } from "../../models/country";
import { LocationType, type ValuesOf } from "../../models/enums";


interface LocationFormProps {
    accountId: number;
    type?: ValuesOf<typeof LocationType>;
    addressOnly?: boolean;
    sx?: SxProps;
}

export const LocationForm = ({ accountId, type, addressOnly = false, sx = { }}: LocationFormProps): JSX.Element => {
    const record = useRecordContext<Location>();
    const { setValue } = useFormContext();
    const { id } = useParams();
    return (
        <Grid container spacing={1} sx={{ width: "100%" }}>
            <Grid size={{ md: 6 }}>
                <TextInput<Location> source="accountId" sx={{ display: "none" }} defaultValue={accountId} />
                {!addressOnly && <TextInput<Location> source="name" validate={required()} />}
                <EnumInput<Location, typeof LocationType>
                    source="type"
                    enum={LocationType}
                    defaultValue={type}
                    sx={{ display: (!!type || !!id) ? "none" : "flex" }}
                />
                {!addressOnly && <TextInput<Location>
                    source="phone"
                    validate={required()}
                />}
                {!addressOnly && <NullableBooleanInput<Location>
                    source="public"
                    validate={required()}
                />}
                <FormDataConsumer<Location>>
                    {({ formData }) => {
                        return formData.type === "Address" ? (
                            <>
                                <AutocompleteInput<Location>
                                    source="country"
                                    validate={required()}
                                    choices={Country.map(x => ({ id: x, name: x }))}
                                    disabled={!!record?.country}
                                />
                                <TextInput<Location>
                                    source="state"
                                    validate={required()}
                                    disabled={!!record?.state}
                                />
                                <TextInput<Location>
                                    source="city"
                                    validate={required()}
                                    disabled={!!record?.city}
                                />
                                <ReferenceInput<Location, Location>
                                    source="externalId"
                                    reference="address"
                                    map={x => ({ mapbox_id: x.externalId, name: x.address } as any)}
                                    filter={{ country: formData.country, state: formData.state, city: formData.city }}
                                >
                                    <AutocompleteInput<Location>
                                        label="Address"
                                        optionValue="externalId"
                                        optionText={(x: Location) => x.city ? `${x.address} (${x.city})` : x.address}
                                        matchSuggestion={(filter, choice: Location) => choice.address.toLowerCase().includes(filter.toLowerCase())}
                                        inputText={(record: Location) => record.address}
                                        source="name"
                                        validate={required()}
                                        disabled={!!record?.address}
                                        filterToQuery={x => ({ address: x })}
                                        debounce={250}
                                        onChange={(_, record) => {
                                            const fields = ["country", "state", "city", "address", "zip", "point"];
                                            if(record) {
                                                fields.forEach(x => {
                                                    setValue(x, record[x]);
                                                });
                                                setValue("name", `${record.address} (${record.city})`);
                                            } else {
                                                fields.forEach(x => {
                                                    setValue(x, undefined);
                                                });
                                            }
                                        }} 
                                    />
                                </ReferenceInput>
                                {/* <TextInput<Location>
                                    source="address"
                                    validate={required()}
                                    disabled={!!record?.address}
                                /> */}

                                <TextInput<Location>
                                    source="number"
                                    validate={required()}
                                    disabled={!!record?.address}
                                />
                                <TextInput<Location>
                                    source="apartment"
                                    disabled={!!record?.apartment}
                                />
                                <TextInput<Location>
                                    source="zip"
                                    validate={required()}
                                    disabled={!!record?.zip}
                                />
                            </>
                        ) : (
                            <></>
                        );
                    }}
                </FormDataConsumer>
            </Grid>
            <Grid size={{ md: 6 }}>
                <MapField source="point" zones="zones" name="name" sx={sx}/>
            </Grid>
        </Grid>
    );
};
